import React from "react";

import { defineMessages, IntlShape } from "react-intl";

import ImportErrorType from "@mapmycustomers/shared/enum/imports/ImportErrorType";

import ButtonLink from "@app/component/ButtonLink";
import openChat from "@app/util/openChat";

export const errorTypeMessages = defineMessages({
  [ImportErrorType.CSV_HEADER_MALFORMED]: {
    id: "import.upload.error.csvHeaderMalformed",
    defaultMessage:
      "Your CSV file is missing headers or they are in the wrong format. Please make sure the file has appropriate headers.",
    description: "Source Card select popup direct api option",
  },
  [ImportErrorType.CSV_INCONSISTENT_RECORD_LENGTH]: {
    id: "import.upload.error.inconsistentFieldsLength",
    defaultMessage:
      "The number of fields in a record is inconsistent with the expected number of fields. Please ensure all records contain the same number of fields and try again.",
    description: "Source Card select popup mobile option",
  },
  [ImportErrorType.CSV_INVALID_CLOSING_QUOTE]: {
    id: "import.upload.error.invalidClosingQuote",
    defaultMessage:
      "A closing quote (”) was found in an unexpected place. Please ensure that each value in the file is correctly enclosed in quotes and try again.",
    description: "Invalid closing quote error message",
  },
  [ImportErrorType.CSV_NON_TRIMABLE_CHAR_AFTER_CLOSING_QUOTE]: {
    id: "import.upload.error.nonTrimmableCharAfterClosingQuote",
    defaultMessage:
      "A closing quote (”) was not followed by a comma or end-of-line character in your CSV file. Please fix and try again.",
    description: "Source Card select popup integrations option",
  },
  [ImportErrorType.CSV_QUOTE_NOT_CLOSED]: {
    id: "import.upload.error.quoteNotClosed",
    defaultMessage:
      "There was an opening quote (”) that wasn’t closed in your CSV file. Please add a closing quote and try again.",
    description: "Source Card select popup lead generation option",
  },
  [ImportErrorType.CSV_RECORD_DONT_MATCH_COLUMNS_LENGTH]: {
    id: "import.upload.error.dontMatchColumnLengths",
    defaultMessage:
      "The number of fields in a record does not match the number of columns specified in the header. Please fix and try again.",
    description: "Source Card select popup imports option",
  },
  [ImportErrorType.DUPE_COLUMN_NAME]: {
    id: "import.upload.error.dupeColumnName",
    defaultMessage: "CSV contains duplicate column names",
    description: "Message for DUPE_COLUMN_NAME error type",
  },
  [ImportErrorType.EMPTY_FILE]: {
    id: "import.upload.error.emptyFile",
    defaultMessage:
      "Your CSV file appeared to be empty. Please make sure the file has data and try again.",
    description: "Source Card select popup direct api option",
  },
  [ImportErrorType.IMPORT_ID_NOT_FOUND]: {
    id: "import.upload.error.importIdNotFound",
    defaultMessage:
      "We couldn't find the unique ID you selected in your CSV file. Please check that the ID has been typed correctly and try again.",
    description: "Source Card select popup direct api option",
  },
  [ImportErrorType.INVALID_OPENING_QUOTE]: {
    id: "import.upload.error.invalidOpeningQuote",
    defaultMessage:
      "An opening quote (”) was found in an unexpected place. Please ensure that each value in the file is correctly enclosed in quotes and try again.",
    description: "Invalid opening quote error message",
  },
  [ImportErrorType.ROW_OVERFLOW_ERROR]: {
    id: "import.upload.error.rowOverflowError",
    defaultMessage:
      "Your CSV file has more than 100,000 rows, which is too large. Please split into smaller files and try again.",
    description: "Source Card select popup direct api option",
  },
  [ImportErrorType.UNKNOWN]: {
    id: "import.upload.error.unknown",
    defaultMessage:
      "Sorry, we didn’t recognize this error. Please <a>contact support</a> for help.",
    description: "Source Card select popup direct api option",
  },
  [ImportErrorType.VALIDATION_FAILED]: {
    id: "import.upload.error.validationFailed",
    defaultMessage:
      "We couldn’t validate the first few records in your CSV file. Please fix and try again.",
    description: "Source Card select popup direct api option",
  },
});

const getErrorTypeMessage = (intl: IntlShape, errorType: ImportErrorType) => {
  return intl.formatMessage(
    errorTypeMessages[errorType] ?? errorTypeMessages[ImportErrorType.UNKNOWN],
    {
      a: (text) => <ButtonLink onClick={openChat}>{text}</ButtonLink>,
    }
  );
};

export default getErrorTypeMessage;
